/**
 * Durable, per-account dismissal state for announcements.
 *
 * Announcements are the only message type whose dismissal survives a reload,
 * because they describe a one-time event rather than an ongoing condition.
 * Keying by account means a shared machine does not suppress an announcement
 * for the next person who signs in.
 *
 * See docs/designs/message-taxonomy.md § 3.2.
 */

const KEY_PREFIX = "excalidraw-ms:announcements:dismissed:";

function storageKey(accountId: string): string {
  return `${KEY_PREFIX}${accountId}`;
}

/**
 * Reads the dismissed ids for an account.
 *
 * Every failure mode - storage disabled, quota errors, corrupt or non-array
 * JSON - degrades to "nothing dismissed" rather than throwing. Showing an
 * announcement one extra time is a far better failure than breaking the editor.
 */
export function readDismissed(accountId: string | null | undefined): string[] {
  if (!accountId) return [];
  try {
    const raw = window.localStorage.getItem(storageKey(accountId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry): entry is string => typeof entry === "string");
  } catch {
    return [];
  }
}

/**
 * Marks an announcement dismissed for an account. Silently gives up if storage
 * is unavailable; the caller has already hidden the banner in local state, so
 * the only consequence is that it reappears on the next load.
 */
export function writeDismissed(accountId: string | null | undefined, id: string): void {
  if (!accountId) return;
  try {
    const next = Array.from(new Set([...readDismissed(accountId), id]));
    window.localStorage.setItem(storageKey(accountId), JSON.stringify(next));
  } catch {
    // Intentionally ignored - see above.
  }
}

/** Test helper. Clears dismissal state for one account. */
export function clearDismissed(accountId: string): void {
  try {
    window.localStorage.removeItem(storageKey(accountId));
  } catch {
    // Intentionally ignored.
  }
}
